import {FocusUtil} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FocusActivator';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {
    inputKeyMapConfig,
    MeshInputKeyboardHandlers
} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/mesh/MeshInputKeyboardHandlers';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {FormulaInput} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/input/FormulaInput';
import {InputDocView} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/input/InputDocView';
import {InputTypeahead} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/input/InputTypeahead';
import {ATextEvent, TextSelection} from '@mintdata/mint-kit/api/models/Text';
import {DomUtils} from '@mintdata/mint-kit/impl/util/DomUtils';
import {bind} from 'bind-decorator';
import {computed} from 'mobx';
import {observer, inject} from 'mobx-react';
import * as React from 'react';
import {HotKeys} from 'react-hotkeys';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {FormulaInputTokenType} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FormulaToken';
import {R1C1Location} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/R1C1';

const styles = require('@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/input/InputView.less');

interface IInputViewProps
{
    ctx?:IWSPMeshContext;
    isShowCellRefHint:boolean;
}

@inject('ctx')
@observer
export class InputView extends React.Component<IInputViewProps, any>
{
    private _inputElement:HTMLDivElement = null;
    private _lastInputText:string = null;
    private _lastSelectionInputText:string = null;

    @bind
    private _onInputRef(inputElement:HTMLDivElement):void
    {
        const {meshInput} = controllers.ui.spreadsheets;
        const {ctx} = this.props;

        this._inputElement = inputElement;

        if( inputElement )
            meshInput.setInputActivator(
                FocusUtil.selectionTextFocusActivator(
                    this._focusInput,
                    this._getText,
                    this._setText,
                    this._getSelection,
                    this._setSelection
                )
            );
        else
            meshInput.setInputActivator(null);
    }

    @bind
    private _focusInput():void
    {
        if( this._inputElement )
            this._inputElement.focus();
    }

    @bind
    private _getText():string
    {
        return DomUtils.getTextContent(this._inputElement);
    }

    @bind
    private _setText(value:string):void
    {
        DomUtils.setTextContent(this._inputElement, value);
        this._lastInputText = value;
    }

    @bind
    private _getSelection():TextSelection
    {
        return DomUtils.getSelection(this._inputElement);
    }

    @bind
    private _setSelection(value:TextSelection):void
    {
        DomUtils.setSelection(this._inputElement, value);
    }

    /*private get _imputBounds():MeshBounds
    {
        const {sheetId} = this.props;
        const meshState = controllers.ui.spreadsheets.mesh.getState(sheetId);
        const {details} = meshState;

    }*/

    @computed
    private get _style():React.CSSProperties
    {
        const {ctx} = this.props;
        const {sheetId, sheetSettingsCtrl} = ctx;
        const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);
        const {details} = meshState;
        const inputState = controllers.ui.spreadsheets.main.getEditorState().input;

        const {colIndex, rowIndex} = details.activeCell;
        const {x, y, width, height} = inputState.activeCellBounds;

        let simpleValue = true;

        if( inputState.formulaTokens )
        {
            const inputTokenType = inputState.formulaTokens[0].token.type;
            simpleValue = inputTokenType === FormulaInputTokenType.SimpleValue;
        }

        let textStyle:React.CSSProperties = {};

        const cellSettings = sheetSettingsCtrl.getCellSettings(
            sheetId,
            new MeshLocation(colIndex, rowIndex).asKey()
        );

        if( cellSettings && simpleValue )
        {
            if( cellSettings.textColor )
                textStyle.color = cellSettings.textColor;

            if( cellSettings.bgColor )
                textStyle.backgroundColor = cellSettings.bgColor;

            if( cellSettings.textBold )
                textStyle.fontWeight = 'bold';

            if( cellSettings.textItalic )
                textStyle.fontStyle = 'italic';

            if( cellSettings.textUnderline )
                textStyle.textDecoration = 'underline';

            if( cellSettings.textAlign )
                textStyle.textAlign = (cellSettings.textAlign as string).toLowerCase();
        }

        return {
            transform: `translate(${x}px, ${y}px)`,
            minWidth: `${Math.min(width, details.visibleWidth)}px`,
            maxWidth: `${details.visibleWidth - x}px`,
            minHeight: `${Math.min(height, details.visibleHeight)}px`,
            maxHeight: `${details.visibleHeight - y}px`,
            // ...cellFont.toCSSValue()
            ...textStyle
        };
    }

    @bind
    private _onTextChanged(event:ATextEvent):void
    {
        const {ctx} = this.props;
        const {meshInput} = controllers.ui.spreadsheets;

        meshInput.setActiveCellFormula(ctx, event.newText, event.selection, true);

        meshInput.setIsInputJustMounted(false);
    }

    @bind
    private _onSelectionChanged(event:ATextEvent):void
    {
        const {ctx} = this.props;
        const {meshInput} = controllers.ui.spreadsheets;

        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(this.props.ctx);

        if( meshState.details.isScrolling ) return;

        const text:string = this._getText();

        if( this._lastSelectionInputText === text )
            meshInput.setTextSelection(ctx, event.selection);

        this._lastSelectionInputText = text;
    }

    @bind
    private _onPaste(textToPaste:string):void
    {
        const {ctx} = this.props;
        const {meshInput} = controllers.ui.spreadsheets;

        meshInput.addTextInInputControl(ctx, textToPaste);
    }

    public componentDidMount():void
    {
    }

    public render():JSX.Element
    {
        const {ctx, isShowCellRefHint} = this.props;

        const {isEditMode} = controllers.ui.spreadsheets.main;

        const editorState = controllers.ui.spreadsheets.main.getEditorState();

        const activeSheet = controllers.ui.spreadsheets.main.getActiveSheet();

        const editSheet = controllers.ui.spreadsheets.main.findAnySheetById(editorState.editSheetId);
        const editCellName = new R1C1Location(
            0, false, 0, false
        ).toA1String(editorState.input.colIndex, editorState.input.rowIndex);

        const hintContent = editSheet && editSheet.name && editorState.editSheetId !== activeSheet.id
            ? `${editSheet.name}!${editCellName}`
            : editCellName;

        return isEditMode &&
            <HotKeys keyMap={inputKeyMapConfig} handlers={MeshInputKeyboardHandlers.getHandlers(ctx)}>
                <div
                    id={ctx.sheetId}
                    style={this._style}
                    className={styles.meshInput}
                >
                    <FormulaInput
                        onTextChanged={this._onTextChanged}
                        onSelectionChanged={this._onSelectionChanged}
                        onPaste={this._onPaste}
                        onRef={this._onInputRef}
                    />
                    {(isShowCellRefHint || editSheet.id !== activeSheet.id) && <div className={styles.cellRefHint} >
                        {hintContent}
                    </div>}
                    <InputTypeahead />
                    <InputDocView formulaInputHeight={parseInt(this._style.minHeight)}/>
                </div>
            </HotKeys>;
    }
}
